'use strict';
/* ════════════════════════════════════════════════════════════
   triQUIP INTELLIGENCE — SHARED APP ENGINE
   Sport-specific behaviour is driven entirely by SPORT_CONFIG,
   PRODUCTS, and CUSTOMER, loaded before this file via <script>.
════════════════════════════════════════════════════════════ */

// ── GLOBAL STATE ──────────────────────────────────────────────
let cartCount = 0;
let displayCount = 12;
let activeFilters = { cat: 'all', gender: 'all', runner: new Set() };
let maxPrice = 50000;
let matchProfileOn = false;

// Generic profile object — fields are populated dynamically based on
// SPORT_CONFIG.profileQuestions, so the same shape works for any sport.
let profile = { hasProfile: false };
function resetProfile() {
  profile = { hasProfile: false };
  SPORT_CONFIG.profileQuestions.forEach(q => {
    if (q.type === 'multi') profile[q.id] = new Set();
    else if (q.type === 'slider') profile[q.id] = 0;
    else profile[q.id] = null;
  });
}
resetProfile();

const STAGE_LABELS = SPORT_CONFIG.stageLabels;

// ════════════════════════════════════════════════════════════
// SPORT CHROME — renders hero, nav, category grid, profile
// questions, and footer text entirely from SPORT_CONFIG.
// This is what makes the same engine work across sports.
// ════════════════════════════════════════════════════════════
function renderSportChrome() {
  // Nav
  document.getElementById('navSub').textContent = SPORT_CONFIG.navSub;
  const navLabels = { shoes: 'Shoes', wearables: 'Technology', apparel: 'Apparel', compression: 'Compression', gear: 'Gear' };
  const navLinksHtml = ['<li><a href="#" class="active" data-cat="all">All</a></li>']
    .concat(SPORT_CONFIG.categories.map(c => `<li><a href="#" data-cat="${c.key}">${navLabels[c.key] || c.name}</a></li>`)).join('');
  document.getElementById('navLinks').innerHTML = navLinksHtml;
  document.querySelectorAll('.nav-links a').forEach(a => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      document.querySelectorAll('.nav-links a').forEach(x => x.classList.remove('active'));
      a.classList.add('active');
      filterCat(a.dataset.cat);
    });
  });

  // Hero
  document.getElementById('heroBgText').textContent = SPORT_CONFIG.label.slice(0, 4).toUpperCase();
  document.getElementById('heroEyebrow').textContent = SPORT_CONFIG.heroEyebrow;
  document.getElementById('heroTitle').innerHTML = SPORT_CONFIG.heroTitle;
  document.getElementById('heroSub').textContent = SPORT_CONFIG.heroSub;
  document.getElementById('heroTrust').innerHTML = SPORT_CONFIG.heroTrust
    .map(t => `<span>${t}</span>`).join('<span class="dot"></span>');

  // Apply accent colour as CSS variable override
  document.documentElement.style.setProperty('--or', SPORT_CONFIG.accentColor);
  document.documentElement.style.setProperty('--or-d', SPORT_CONFIG.accentColorD);

  // Category section title + grid
  const personNoun = { Running: 'RUNNER', Cycling: 'CYCLIST', Triathlon: 'DISCIPLINE' }[SPORT_CONFIG.label] || SPORT_CONFIG.label.toUpperCase();
  document.getElementById('catSectionTitle').innerHTML =
    `EVERYTHING FOR<br>EVERY <span class="hl">${personNoun}</span>`;
  document.getElementById('catGridInner').innerHTML = SPORT_CONFIG.categories.map(c => `
    <div class="cat-card" data-cat="${c.key}" onclick="filterCat('${c.key}')">
      ${c.img ? `<img class="cat-card-img" src="${c.img}" alt="${c.name}" loading="lazy">` : ''}
      <div class="cat-card-overlay"></div>
      <div class="cat-card-body">
        <div class="cat-card-name">${c.name}</div>
        <div class="cat-card-count">${c.count}</div>
      </div>
    </div>`).join('');

  // Sidebar filters
  document.getElementById('catFilterChips').innerHTML =
    `<div class="fchip active" data-f="cat" data-v="all" onclick="toggleFilter(this)">All</div>` +
    SPORT_CONFIG.categories.map(c => `<div class="fchip" data-f="cat" data-v="${c.key}" onclick="toggleFilter(this)">${c.name}</div>`).join('');

  document.getElementById('genderFilterChips').innerHTML =
    `<div class="fchip active" data-f="gender" data-v="all" onclick="toggleFilter(this)">All</div>
     <div class="fchip" data-f="gender" data-v="men" onclick="toggleFilter(this)">Men's</div>
     <div class="fchip" data-f="gender" data-v="women" onclick="toggleFilter(this)">Women's</div>`;

  // Discipline filter chips — pulled from the discipline question's options if present
  const discQ = SPORT_CONFIG.profileQuestions.find(q => q.id === 'discipline' || q.id === 'goal');
  document.getElementById('disciplineFilterLabel').textContent = SPORT_CONFIG.key === 'triathlon' ? 'Race Distance' : 'Discipline / Goal';
  if (discQ) {
    const opts = discQ.options.map(o => ({ v: o.v, label: o.title || o.label }));
    document.getElementById('runnerTypeChips').innerHTML = opts
      .map(o => `<div class="fchip" data-f="runner" data-v="${o.v}" onclick="toggleFilter(this)">${o.label}</div>`).join('');
  }

  // Profile panel title
  document.getElementById('ppTitle').textContent = `${SPORT_CONFIG.label} Profile`;

  // Profile questions — fully dynamic
  document.getElementById('ppBody').innerHTML = SPORT_CONFIG.profileQuestions.map(q => renderQuestionBlock(q)).join('');

  // Footer
  document.getElementById('ftTagline').textContent =
    `The world's smartest ${SPORT_CONFIG.label.toLowerCase()} ecosystem. Coach-vetted gear, matched to your actual journey.`;
  document.getElementById('ftCatList').innerHTML = SPORT_CONFIG.categories.map(c => `<li><a href="#" onclick="filterCat('${c.key}')">${c.name}</a></li>`).join('');

  // Price slider max — scale by sport (cycling/triathlon have pricier items)
  const sliderMax = SPORT_CONFIG.key === 'running' ? 50000 : SPORT_CONFIG.key === 'cycling' ? 300000 : 300000;
  const slider = document.getElementById('priceSlider');
  slider.max = sliderMax; slider.value = sliderMax; maxPrice = sliderMax;
  document.getElementById('priceLabel').textContent = '₹' + sliderMax.toLocaleString('en-IN');

  document.title = `triQUIP ${SPORT_CONFIG.label} Intelligence`;
}

function renderQuestionBlock(q) {
  const optionalTag = q.optional ? '<span class="pp-optional-tag">Optional</span>' : '';
  let body = '';

  if (q.type === 'single') {
    body = `<div class="pp-opts" id="q-${q.id}">${q.options.map(o => `
      <div class="pp-opt" data-v="${o.v}" onclick="selectProfile(this,'${q.id}')">
        <div class="pp-opt-text"><div class="pp-opt-title">${o.title}</div><div class="pp-opt-sub">${o.sub}</div></div>
      </div>`).join('')}</div>`;
  } else if (q.type === 'multi') {
    const cls = q.grid ? 'pp-opts pp-opts-grid' : 'pp-opts';
    body = `<div class="${cls}" id="q-${q.id}">${q.options.map(o => `
      <div class="pp-opt-sm" data-v="${o.v}" onclick="toggleMultiProfile(this,'${q.id}')">${o.label}</div>`).join('')}</div>`;
  } else if (q.type === 'singleSmall') {
    body = `<div class="pp-opts pp-opts-grid" id="q-${q.id}">${q.options.map(o => `
      <div class="pp-opt-sm" data-v="${o.v}" onclick="selectProfileSingle(this,'${q.id}','q-${q.id}')">${o.label}</div>`).join('')}</div>`;
  } else if (q.type === 'slider') {
    body = `<div class="pp-slider-box">
      <div class="pp-slider-val" id="slider-${q.id}-display">${q.labels[0]}</div>
      <input type="range" class="pp-range" id="slider-${q.id}" min="0" max="${q.labels.length - 1}" value="0" oninput="updateGenericSlider(this,'${q.id}')">
      <div class="pp-slider-marks">${q.labels.map(l => `<span>${l}</span>`).join('')}</div>
    </div>`;
  }

  return `<div class="pp-q">
    <div class="pp-q-label">${q.label} ${optionalTag}</div>
    <div class="pp-q-hint">${q.hint}</div>
    ${body}
  </div>`;
}

// ════════════════════════════════════════════════════════════
// ANNOUNCEMENT TICKER
// ════════════════════════════════════════════════════════════
function getAnnItems() {
  const sportLower = SPORT_CONFIG.label.toLowerCase();
  return [
    `triQUIP ${SPORT_CONFIG.label} Intelligence — recommendations matched to YOUR journey`,
    `Coach-recommended picks — curated from real race and training experience`,
    `Free shipping pan-India · 72h delivery`,
    `Build your profile in 90 seconds — totally optional, always skippable`,
    `Browse the full ${sportLower} catalogue with zero login required`,
  ];
}
function renderTicker() {
  const items = [...getAnnItems(), ...getAnnItems()].map(t =>
    `<span class="ti">${t}</span><span class="tdot"></span>`).join('');
  document.getElementById('annInner').innerHTML = items;
}

// ════════════════════════════════════════════════════════════
// NAV / SEARCH
// ════════════════════════════════════════════════════════════
function toggleSearch() {
  document.getElementById('searchOverlay').classList.toggle('show');
  if (document.getElementById('searchOverlay').classList.contains('show')) {
    setTimeout(() => document.getElementById('searchInput').focus(), 100);
  }
}
function liveSearch(q) {
  const results = document.getElementById('searchResults');
  if (!q || q.length < 2) { results.innerHTML = ''; return; }
  const matches = PRODUCTS.filter(p =>
    p.name.toLowerCase().includes(q.toLowerCase()) ||
    p.vendor.toLowerCase().includes(q.toLowerCase()) ||
    p.cat.toLowerCase().includes(q.toLowerCase())
  ).slice(0, 8);
  results.innerHTML = matches.length ? matches.map(p => `
    <div class="sr-item" onclick="openQuickView('${p.id}');toggleSearch()">
      <div><div class="sr-name">${p.name}</div><div class="sr-cat">${p.cat}</div></div>
      <div class="sr-price">₹${p.price.toLocaleString('en-IN')}</div>
    </div>`).join('') :
    `<div style="text-align:center;padding:30px;color:var(--dm);font-size:13px">No products match "${q}"</div>`;
}

// ════════════════════════════════════════════════════════════
// INVITE STRIP — dismissible, non-blocking
// ════════════════════════════════════════════════════════════
function dismissInvite() {
  document.getElementById('inviteStrip').classList.add('hidden');
  sessionStorage.setItem('triquip_invite_dismissed', '1');
}
function checkInviteState() {
  if (sessionStorage.getItem('triquip_invite_dismissed') || profile.hasProfile) {
    document.getElementById('inviteStrip').classList.add('hidden');
  }
}

// ════════════════════════════════════════════════════════════
// CATEGORY FILTERING (browse, zero gate)
// ════════════════════════════════════════════════════════════
function filterCat(cat) {
  activeFilters.cat = cat;
  document.querySelectorAll('#catFilterChips .fchip').forEach(c => {
    c.classList.toggle('active', c.dataset.v === cat);
  });
  displayCount = 12;
  document.getElementById('catGrid').scrollIntoView({ behavior: 'smooth', block: 'start' });
  setTimeout(() => document.querySelector('.shop-section').scrollIntoView({ behavior: 'smooth' }), 200);
  renderGrid();
}

function toggleFilter(el) {
  const f = el.dataset.f, v = el.dataset.v;
  if (f === 'cat') {
    document.querySelectorAll('#catFilterChips .fchip').forEach(c => c.classList.remove('active'));
    el.classList.add('active');
    activeFilters.cat = v;
  } else if (f === 'gender') {
    document.querySelectorAll('#genderFilterChips .fchip').forEach(c => c.classList.remove('active'));
    el.classList.add('active');
    activeFilters.gender = v;
  } else {
    el.classList.toggle('active');
    if (el.classList.contains('active')) activeFilters.runner.add(v);
    else activeFilters.runner.delete(v);
  }
  displayCount = 12;
  renderGrid();
  renderActiveTags();
}

function renderActiveTags() {
  const tags = [];
  if (activeFilters.cat !== 'all') tags.push({ type: 'cat', v: activeFilters.cat });
  if (activeFilters.gender !== 'all') tags.push({ type: 'gender', v: activeFilters.gender === 'men' ? "Men's" : "Women's" });
  activeFilters.runner.forEach(v => tags.push({ type: 'runner', v }));
  const el = document.getElementById('activeTags');
  el.innerHTML = tags.map(t => `<div class="atag" onclick="removeTag('${t.type}','${t.v}')">${t.v}</div>`).join('');
}
function removeTag(type, v) {
  if (type === 'cat') {
    activeFilters.cat = 'all';
    document.querySelectorAll('#catFilterChips .fchip').forEach(c => c.classList.toggle('active', c.dataset.v === 'all'));
  } else if (type === 'gender') {
    activeFilters.gender = 'all';
    document.querySelectorAll('#genderFilterChips .fchip').forEach(c => c.classList.toggle('active', c.dataset.v === 'all'));
  } else {
    activeFilters.runner.delete(v);
    document.querySelectorAll('#runnerTypeChips .fchip').forEach(c => { if (c.dataset.v === v) c.classList.remove('active'); });
  }
  renderGrid();
  renderActiveTags();
}

function onPriceChange(el) {
  maxPrice = +el.value;
  const pct = (el.value / el.max * 100).toFixed(1);
  el.style.setProperty('--v', pct + '%');
  document.getElementById('priceLabel').textContent = '₹' + (+el.value).toLocaleString('en-IN');
  renderGrid();
}

function clearFilters() {
  activeFilters = { cat: 'all', gender: 'all', runner: new Set() };
  maxPrice = 50000;
  document.querySelectorAll('.fchip').forEach(c => c.classList.toggle('active', c.dataset.v === 'all'));
  document.getElementById('priceSlider').value = 50000;
  document.getElementById('priceLabel').textContent = '₹50,000';
  if (document.getElementById('matchProfileToggle')) {
    document.getElementById('matchProfileToggle').checked = false;
    matchProfileOn = false;
  }
  renderActiveTags();
  renderGrid();
}

function toggleProfileMatch(el) {
  matchProfileOn = el.checked;
  renderGrid();
}

// ════════════════════════════════════════════════════════════
// PRODUCT MATCHING — score against profile (used for sort + badges)
// ════════════════════════════════════════════════════════════

// Gender is a hard filter, not a soft score — showing a men's-fit shoe to
// someone shopping women's (or vice versa) isn't just "less relevant," it's
// usually the wrong size/last entirely. 'unisex' on either side (profile or
// product) always passes.
function genderOk(p) {
  if (!profile.gender || profile.gender === 'unisex') return true;
  if (!p.gender || p.gender === 'unisex') return true;
  return p.gender === profile.gender;
}

// Sidebar/browse gender filter — separate from the profile quiz's genderOk,
// since a shopper should be able to filter by gender immediately without
// completing the full profile flow.
function genderFilterOk(p) {
  if (activeFilters.gender === 'all') return true;
  if (!p.gender || p.gender === 'unisex') return true;
  return p.gender === activeFilters.gender;
}

// Budget tiers mapped to real numeric price ranges. This is deliberately
// independent of the `budgetTier` product metafield (which requires manual
// per-product data entry that hasn't been done yet) — it filters directly
// against each product's actual Shopify price, so it works correctly with
// zero additional setup, and keeps working even if the metafield is never filled in.
const BUDGET_RANGES = {
  under5k: [0, 5000],
  '5to10k': [5000, 10000],
  '10to15k': [10000, 15000],
  '15kplus': [15000, Infinity],
};
function budgetOk(p) {
  if (!profile.budget) return true;
  const range = BUDGET_RANGES[profile.budget];
  if (!range) return true;
  return p.price >= range[0] && p.price <= range[1];
}

function profileMatchScore(p) {
  if (!profile.hasProfile) return 0;
  let score = 0;

  // stage
  if (profile.stage && p.stage && p.stage.includes(profile.stage)) score += 40;

  // discipline (multi-select set) — running, cycling, triathlon "discipline" field
  if (profile.discipline && profile.discipline.size && p.discipline) {
    const overlap = [...profile.discipline].filter(d => p.discipline.includes(d));
    score += overlap.length * 15;
  }

  // goal — running/cycling use this; triathlon uses raceDistance as the goal-equivalent
  if (profile.goal && p.goal && p.goal.includes(profile.goal)) score += 25;
  if (profile.raceDistance && p.goal && p.goal.includes(profile.raceDistance)) score += 25;

  // weakLeg (triathlon-specific) — boost products matching the discipline they're weakest in
  if (profile.weakLeg && profile.weakLeg !== 'balanced') {
    const legCatMap = { swim: 'wetsuits', bike: 'bikes', run: 'shoes' };
    if (p.cat === legCatMap[profile.weakLeg]) score += 20;
  }

  // constraints (injury/comfort issues) — generic field name across all sports
  if (profile.constraints && profile.constraints.size && p.constraintSafe) {
    const hasNone = profile.constraints.has('none');
    if (!hasNone) {
      const overlap = [...profile.constraints].filter(i => p.constraintSafe.includes(i));
      score += overlap.length * 20;
    }
  }

  // Budget: reward products priced comfortably within the chosen range,
  // not just technically under the ceiling — a ₹4,800 shoe is a better
  // "under ₹5,000" match than a ₹4,999 one only in the sense that it leaves
  // more headroom, but both should clearly outscore anything priced higher.
  if (profile.budget && BUDGET_RANGES[profile.budget]) {
    const [min, max] = BUDGET_RANGES[profile.budget];
    if (p.price >= min && p.price <= max) score += 20;
  }
  // Pronation (shoes-specific) — checked against a dedicated metafield if
  // it's been filled in, falling back to a keyword match against name/why
  // text so it still works immediately with zero manual data entry, same
  // pattern as watch priority above. Explicitly skipped for 'unsure', since
  // that means the customer opted out rather than picked a real answer.
  if (profile.pronation && profile.pronation !== 'unsure' && p.cat === 'shoes') {
    if (p.pronationSupport && p.pronationSupport.includes(profile.pronation)) {
      score += 30;
    } else {
      const haystack = `${p.name} ${p.why || ''}`.toLowerCase();
      const PRONATION_KEYWORDS = {
        overpronation: ['stability', 'motion control', 'overpronation', 'support'],
        neutral: ['neutral', 'responsive', 'balanced cushion'],
        supination: ['flexible', 'high cushion', 'underpronation', 'supination', 'extra cushion'],
      };
      const words = PRONATION_KEYWORDS[profile.pronation] || [];
      if (words.some(w => haystack.includes(w))) score += 18;
    }
  }

  // Watch priority (wearables-specific) — keyword match against name/why text.
  // Best-effort: works even with no metafield data filled in, and improves
  // automatically once `why` copy gets written for more products.
  if (profile.watchPriority && profile.watchPriority.size && p.cat === 'wearables') {
    const haystack = `${p.name} ${p.why || ''}`.toLowerCase();
    const KEYWORDS = {
      battery: ['battery', 'days of battery', 'long-lasting'],
      gps: ['gps', 'accuracy', 'satellite', 'tracking'],
      music: ['music', 'spotify', 'payments', 'nfc', 'contactless'],
      simplicity: ['simple', 'easy', 'lightweight', 'minimal'],
    };
    let watchMatches = 0;
    profile.watchPriority.forEach(pref => {
      const words = KEYWORDS[pref] || [];
      if (words.some(w => haystack.includes(w))) watchMatches++;
    });
    score += watchMatches * 18;
  }

  if (p.curator) score += 5;
  score += (p.rating || 0) * 2;
  return score;
}


// ════════════════════════════════════════════════════════════
// MAIN GRID RENDERING
// ════════════════════════════════════════════════════════════
function renderGrid() {
  let items = PRODUCTS.filter(p => {
    if (activeFilters.cat !== 'all' && p.cat !== activeFilters.cat) return false;
    if (p.price > maxPrice) return false;
    if (!genderOk(p)) return false;
    if (!genderFilterOk(p)) return false;
    if (activeFilters.runner.size) {
      const matches = [...activeFilters.runner].some(r => (p.goal && p.goal.includes(r)) || (p.discipline && p.discipline.includes(r)));
      if (!matches) return false;
    }
    return true;
  });

  const sortVal = document.getElementById('sortSelect') ? document.getElementById('sortSelect').value : 'featured';
  if (matchProfileOn && profile.hasProfile) {
    items = items.filter(p => budgetOk(p));
    items = [...items].sort((a, b) => profileMatchScore(b) - profileMatchScore(a));
  } else if (sortVal === 'price-asc') items = [...items].sort((a, b) => a.price - b.price);
  else if (sortVal === 'price-desc') items = [...items].sort((a, b) => b.price - a.price);
  else if (sortVal === 'rating') items = [...items].sort((a, b) => b.rating - a.rating);
  else items = [...items].sort((a, b) => (b.curator ? 1 : 0) - (a.curator ? 1 : 0));

  document.getElementById('resultCount').textContent = items.length;
  const shown = items.slice(0, displayCount);
  const grid = document.getElementById('productGrid');

  if (!shown.length) {
    grid.innerHTML = `<div class="empty-state"><div class="empty-title">No products match these filters</div><div class="empty-desc">Try clearing a filter or widening your price range.</div></div>`;
  } else {
    grid.innerHTML = shown.map(p => makeProductCard(p)).join('');
  }

  document.getElementById('loadMoreBtn').style.display = items.length > displayCount ? 'inline-block' : 'none';

  // show profile filter toggle if profile exists
  document.getElementById('profileFilterGroup').style.display = profile.hasProfile ? 'block' : 'none';
}

function makeProductCard(p) {
  const matchScore = profileMatchScore(p);
  const showMatch = profile.hasProfile && matchScore > 60;
  let flag = '';
  if (showMatch) flag = '<div class="pc-flag f-match">Matches You</div>';
  else if (p.curator) flag = '<div class="pc-flag f-curator">Coach Pick</div>';

  return `
    <div class="pcard" onclick="openQuickView('${p.id}')">
      <div class="pc-img" style="background:linear-gradient(135deg,#F2EDE6,#E9E4DC)">
        ${flag}
        <div class="pc-acts">
          <button class="pc-qv" onclick="event.stopPropagation();openQuickView('${p.id}')">Quick View</button>
          <button class="pc-add" onclick="event.stopPropagation();addToCart('${p.id}')">+ Add</button>
        </div>
      </div>
      <div class="pc-body">
        <div class="pc-vendor">${p.vendor}</div>
        <div class="pc-name">${p.name}</div>
        <div class="pc-tags">
          ${(p.discipline||[]).slice(0,2).map(d=>`<span class="ptag">${d}</span>`).join('')}
        </div>
        <div class="pc-why">${p.why}</div>
        <div class="pc-foot">
          <div class="pc-price">₹${p.price.toLocaleString('en-IN')}</div>
          <div class="pc-rat">${p.rating}/5</div>
        </div>
      </div>
    </div>`;
}

function loadMore() {
  displayCount += 12;
  renderGrid();
}

// ════════════════════════════════════════════════════════════
// CURATOR RAIL — always visible regardless of profile
// ════════════════════════════════════════════════════════════
function renderCuratorRail() {
  const items = getCuratorProducts().slice(0, 4);
  document.getElementById('curatorRail').innerHTML = items.map(p => makeProductCard(p)).join('');
}

// ════════════════════════════════════════════════════════════
// WELCOME BACK ENGINE — purchase history, ratings, colour nudges
// ════════════════════════════════════════════════════════════
let pendingRating = { orderId: null, stars: 0 };

function openWelcomeBack() {
  renderWelcomeBack();
  document.getElementById('wbOverlay').classList.add('show');
  document.getElementById('wbPanel').classList.add('show');
  document.body.style.overflow = 'hidden';
}
function closeWelcomeBack() {
  document.getElementById('wbOverlay').classList.remove('show');
  document.getElementById('wbPanel').classList.remove('show');
  document.body.style.overflow = '';
}

function renderWelcomeBack() {
  document.getElementById('wbGreeting').textContent = `Welcome back, ${CUSTOMER.name}`;

  const recent = getMostRecentOrder();
  const recentProduct = getProductById(recent.productId);
  const unrated = getUnratedOrders();

  let html = '';

  // ── 1. Most recent purchase recall ──
  html += `
    <div class="wb-order-card">
      <div class="wb-order-top">
        <div class="wb-order-info">
          <div class="wb-order-name">${recentProduct.name}</div>
          <div class="wb-order-meta">
            ${recent.colour ? `<span class="colour-dot"></span>${recent.colour} · ` : ''}
            Ordered ${formatDaysAgo(recent.daysAgo)}
          </div>
        </div>
      </div>
      ${renderRatingBlock(recent)}
      ${renderColourNudge(recent, recentProduct)}
    </div>`;

  // ── 2. Other unrated orders (if more than one pending) ──
  const otherUnrated = unrated.filter(o => o.id !== recent.id);
  if (otherUnrated.length) {
    html += `<div class="wb-next-label" style="margin-top:4px">Also waiting on your feedback</div>`;
    otherUnrated.forEach(o => {
      const p = getProductById(o.productId);
      html += `
        <div class="wb-order-card">
          <div class="wb-order-top">
            <div class="wb-order-info">
              <div class="wb-order-name">${p.name}</div>
              <div class="wb-order-meta">Ordered ${formatDaysAgo(o.daysAgo)}</div>
            </div>
          </div>
          ${renderRatingBlock(o)}
        </div>`;
    });
  }

  // ── 3. Non-mechanical "what's next" — reasoned from the actual purchase ──
  html += renderReasonedNextPick(recent, recentProduct);

  html += `<div class="wb-footer-link"><a onclick="closeWelcomeBack();openProfile()">Or update your full running profile instead</a></div>`;

  document.getElementById('wbBody').innerHTML = html;
}

function formatDaysAgo(days) {
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  return `${months} month${months > 1 ? 's' : ''} ago`;
}

function renderRatingBlock(order) {
  if (order.rating !== null) {
    // Already rated — show it, don't re-ask
    const stars = order.rating + '/5';
    return `
      <div class="wb-existing-rating">
        <span class="wb-existing-stars">${stars}</span>
        ${order.reviewText ? `<span class="wb-existing-text">"${order.reviewText}"</span>` : ''}
      </div>`;
  }
  // Not yet rated — prompt
  return `
    <div class="wb-rate-prompt" id="rateBlock-${order.id}">
      <div class="wb-rate-label">How was it? Your rating helps other runners — and sharpens what we recommend you next.</div>
      <div class="wb-stars" id="stars-${order.id}">
        ${[1,2,3,4,5].map(n => `<span class="wb-star" data-n="${n}" onclick="setStarRating('${order.id}',${n})">${n}</span>`).join('')}
      </div>
      <textarea class="wb-rate-textarea" id="text-${order.id}" placeholder="Optional — what worked, what didn't?"></textarea>
      <button class="wb-rate-submit" id="submit-${order.id}" disabled onclick="submitRating('${order.id}')">Submit Rating</button>
      <button class="wb-rate-skip" onclick="skipRating('${order.id}')">Skip for now</button>
    </div>`;
}

function setStarRating(orderId, n) {
  document.querySelectorAll(`#stars-${orderId} .wb-star`).forEach(s => {
    s.classList.toggle('active', +s.dataset.n <= n);
  });
  pendingRating = { orderId, stars: n };
  document.getElementById(`submit-${orderId}`).disabled = false;
}

function submitRating(orderId) {
  const order = CUSTOMER.orders.find(o => o.id === orderId);
  const text = document.getElementById(`text-${orderId}`).value.trim();
  order.rating = pendingRating.stars;
  order.reviewText = text || null;

  // Re-render the block to show the "thank you" state, then refresh whole panel after a beat
  const block = document.getElementById(`rateBlock-${orderId}`);
  if (block) {
    block.innerHTML = `<div style="font-size:12.5px;color:var(--grn);font-weight:600">Thanks — that helps. Updating your recommendations...</div>`;
  }
  setTimeout(() => renderWelcomeBack(), 900);
}

function skipRating(orderId) {
  const block = document.getElementById(`rateBlock-${orderId}`);
  if (block) block.style.display = 'none';
}

function renderColourNudge(order, product) {
  if (!hasColourVariants(product.id) || !order.colour) return '';
  const otherColours = getOtherColours(product.id, order.colour);
  if (!otherColours.length) return '';
  return `
    <div class="wb-colour-nudge">
      <div class="wb-colour-text">
        <strong>Loved the fit? Same shoe, fresh colour.</strong>
        <span>You picked ${order.colour} last time — here's what else is in stock.</span>
      </div>
      <div class="wb-colour-swatches">
        ${otherColours.slice(0, 3).map(c =>
          `<div class="wb-swatch-chip" onclick="addToCartWithColour('${product.id}','${c}')">${c}</div>`
        ).join('')}
      </div>
    </div>`;
}

function addToCartWithColour(productId, colour) {
  const p = getProductById(productId);
  cartCount++;
  document.getElementById('cartBadge').textContent = cartCount;
  document.getElementById('sbName').textContent = `${p.name} — ${colour}`;
  document.getElementById('sbPrice').textContent = '₹' + p.price.toLocaleString('en-IN');
  const bar = document.getElementById('stickyBar');
  bar.classList.add('show');
  clearTimeout(bar._t);
  bar._t = setTimeout(() => bar.classList.remove('show'), 4500);
}

/* ── THE NON-MECHANICAL PART ──
   Reasoning chain instead of a flat re-score:
   1. What did they buy + how did they rate it (if rated)
   2. What category is it, and what's the logical NEXT step
      (not "more of the same" — a complementary or upgrade path)
   3. Branches are defined per-sport in SPORT_CONFIG.reasoningRules
      so running/cycling/triathlon each reason differently about
      what "the next sensible thing" looks like.
*/
function renderReasonedNextPick(order, product) {
  const helpers = { pickBestFrom, pickAlternative, PRODUCTS, CUSTOMER, getProductById };
  const { headline, reasoning, nextProduct } = SPORT_CONFIG.reasoningRules(order, product, helpers);

  if (!nextProduct) return '';

  return `
    <div class="wb-next-section">
      <div class="wb-next-label">What We'd Suggest Next</div>
      <div class="wb-next-headline">${headline}</div>
      <div class="wb-next-reasoning">${reasoning}</div>
      <div class="wb-next-card" onclick="openQuickView('${nextProduct.id}')">
        <div class="wb-next-card-body">
          <div class="wb-next-card-vendor">${nextProduct.vendor}</div>
          <div class="wb-next-card-name">${nextProduct.name}</div>
          <div class="wb-next-card-why">${nextProduct.why}</div>
        </div>
        <div class="wb-next-card-price">₹${nextProduct.price.toLocaleString('en-IN')}</div>
      </div>
      <button class="wb-next-add" onclick="addToCart('${nextProduct.id}')">+ Add to Cart</button>
    </div>`;
}

function pickBestFrom(cat, excludeProduct) {
  const pool = PRODUCTS.filter(p => p.cat === cat && genderOk(p) && (!excludeProduct || p.id !== excludeProduct.id));
  if (!pool.length) return null;
  // prefer curator items, then rating
  return [...pool].sort((a, b) => (b.curator - a.curator) || (b.rating - a.rating))[0];
}

function pickAlternative(cat, currentProduct) {
  // Pick a different product in the same category, ranked by rating
  const pool = PRODUCTS.filter(p => p.cat === cat && genderOk(p) && p.id !== currentProduct.id);
  return [...pool].sort((a, b) => b.rating - a.rating)[0];

}


function openProfile() {
  document.getElementById('profilePanel').classList.add('open');
  document.getElementById('panelOverlay').classList.add('show');
  document.body.style.overflow = 'hidden';
}
function closeProfile() {
  document.getElementById('profilePanel').classList.remove('open');
  document.getElementById('panelOverlay').classList.remove('show');
  document.body.style.overflow = '';
}

function selectProfile(el, field) {
  const group = el.parentElement;
  group.querySelectorAll('.pp-opt').forEach(o => o.classList.remove('sel'));
  el.classList.add('sel');
  profile[field] = el.dataset.v;
  updateProgress();
}

function selectProfileSingle(el, field, groupId) {
  document.querySelectorAll(`#${groupId} .pp-opt-sm`).forEach(o => o.classList.remove('sel'));
  el.classList.add('sel');
  profile[field] = el.dataset.v;
  updateProgress();
}

function toggleMultiProfile(el, field) {
  el.classList.toggle('sel');
  const v = el.dataset.v;
  const groupId = `q-${field}`;
  // Generic "none" exclusivity — if a question has a "none" option, selecting it
  // clears all other selections in that same group, and vice versa.
  if (v === 'none') {
    if (el.classList.contains('sel')) {
      document.querySelectorAll(`#${groupId} .pp-opt-sm`).forEach(o => { if (o.dataset.v !== 'none') o.classList.remove('sel'); });
      profile[field].clear();
      profile[field].add('none');
    } else {
      profile[field].delete('none');
    }
  } else {
    const noneChip = document.querySelector(`#${groupId} .pp-opt-sm[data-v="none"]`);
    if (noneChip) { noneChip.classList.remove('sel'); profile[field].delete('none'); }
    if (el.classList.contains('sel')) profile[field].add(v);
    else profile[field].delete(v);
  }
  updateProgress();
}

function updateGenericSlider(el, fieldId) {
  const v = +el.value;
  profile[fieldId] = v;
  const max = +el.max;
  const pct = (v / max * 100).toFixed(1);
  el.style.setProperty('--v', pct + '%');
  const q = SPORT_CONFIG.profileQuestions.find(x => x.id === fieldId);
  document.getElementById(`slider-${fieldId}-display`).textContent = q.labels[v];
  updateProgress();
}

function updateProgress() {
  const questions = SPORT_CONFIG.profileQuestions;
  let filled = 0;
  questions.forEach(q => {
    const val = profile[q.id];
    if (q.type === 'multi') { if (val && val.size) filled++; }
    else if (q.type === 'slider') { if (val && val > 0) filled++; }
    else { if (val) filled++; }
  });
  const pct = Math.round((filled / questions.length) * 100);
  document.getElementById('ppProgressFill').style.width = pct + '%';
  document.getElementById('ppProgressText').textContent =
    pct === 0 ? "0% complete — and that's fine" :
    pct === 100 ? "100% complete — let's build your picks" :
    `${pct}% complete — keep going or skip ahead`;
}



// ════════════════════════════════════════════════════════════
// RECOMMENDATION ENGINE — the core intelligence
// ════════════════════════════════════════════════════════════
function generateRecommendations() {
  profile.hasProfile = true;
  closeProfile();

  // Update nav pill + floating button to reflect profile state
  document.getElementById('profilePill').classList.add('has-profile');
  document.getElementById('profilePillText').textContent = 'Profile Active';
  document.getElementById('profileDot').style.background = 'var(--grn)';
  document.getElementById('floatBtn').classList.add('has-profile');
  document.getElementById('floatBtnText').textContent = 'Update Profile';

  dismissInvite();
  showResultsModal();
  renderPersonalSection();
  renderGrid(); // refresh badges
}

function scoreForCategory(cat, limit = 3) {
  return PRODUCTS
    .filter(p => p.cat === cat && genderOk(p) && genderFilterOk(p) && budgetOk(p))
    .map(p => ({ p, score: profileMatchScore(p) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(x => x.p);
}

function showResultsModal() {
  const stage = profile.stage || SPORT_CONFIG.stageOrder[1];
  const stageLabel = STAGE_LABELS[stage];
  const stageOrder = SPORT_CONFIG.stageOrder;
  const stageIdx = stageOrder.indexOf(stage);

  // Pull the top 3 categories from SPORT_CONFIG for the result sections —
  // first category is the "primary" gear type, next two are supporting
  const catKeys = SPORT_CONFIG.categories.map(c => c.key);
  const primaryCat = catKeys[0];
  const secondaryCat = catKeys.find(k => k.includes('wearable')) || catKeys[1];
  const remainingCats = catKeys.filter(k => k !== primaryCat && k !== secondaryCat);

  const primaryItems = scoreForCategory(primaryCat, 3);
  const secondaryItems = scoreForCategory(secondaryCat, 2);
  const tailItems = remainingCats.flatMap(c => scoreForCategory(c, 2)).slice(0, 3);

  // Goal text — find the matching question (goal or raceDistance) and look up its option label
  const goalQ = SPORT_CONFIG.profileQuestions.find(q => q.id === 'goal' || q.id === 'raceDistance');
  const goalVal = profile.goal || profile.raceDistance;
  const goalOpt = goalQ && goalVal ? goalQ.options.find(o => o.v === goalVal) : null;
  const goalText = goalOpt ? (goalOpt.sub || goalOpt.title).toLowerCase() : `your ${SPORT_CONFIG.label.toLowerCase()} goals`;

  // Volume text — find the slider question generically
  const volQ = SPORT_CONFIG.profileQuestions.find(q => q.type === 'slider');
  const volText = volQ && profile[volQ.id] ? `, averaging ${volQ.labels[profile[volQ.id]]}${SPORT_CONFIG.key === 'running' ? '' : '/week'}` : '';

  const constraintsNote = (profile.constraints && profile.constraints.size && !profile.constraints.has('none'))
    ? `<div style="background:rgba(255,80,0,.06);border:1px solid rgba(255,80,0,.2);padding:14px 16px;margin-bottom:20px;font-size:12.5px;color:var(--bd);line-height:1.6"><strong style="color:var(--or)">Note:</strong> Based on the ${[...profile.constraints].join(', ')} history you shared, we've deprioritised products without specific support for that — but always confirm with a physio or coach before changing gear if you're mid-recovery.</div>`
    : '';

  const catLabel = (key) => SPORT_CONFIG.categories.find(c => c.key === key)?.name || key;

  document.getElementById('rmContent').innerHTML = `
    <div class="rm-badge">Your Personalised ${SPORT_CONFIG.label} Profile</div>
    <h2 class="rm-title">Built for a ${stageLabel}</h2>
    <p class="rm-sub">Training towards ${goalText}${volText}. Here's what we'd recommend right now — and what comes next as you progress.</p>

    <div class="rm-stage-bar">
      ${stageOrder.map((s, i) => `<div class="rm-stage-step ${i === stageIdx ? 'active' : ''}"><div class="rm-stage-step-label">${STAGE_LABELS[s]}</div></div>`).join('')}
    </div>

    ${constraintsNote}

    <div class="rm-section-title">${catLabel(primaryCat)} for Right Now</div>
    <div class="rm-grid">${primaryItems.map(makeRmCard).join('')}</div>

    <div class="rm-section-title">${catLabel(secondaryCat)} Matched To Your Stage</div>
    <div class="rm-grid">${secondaryItems.map(makeRmCard).join('')}</div>

    <div class="rm-section-title">Rounding Out Your Setup</div>
    <div class="rm-grid">${tailItems.map(makeRmCard).join('')}</div>

    <div class="rm-wa-strip">
      <div class="rm-wa-text">
        <strong>Want a coach to sanity-check this?</strong>
        <span>10-minute WhatsApp chat refines everything against your exact setup, budget, and race date.</span>
      </div>
      <button class="btn-wa" onclick="openWA()">WhatsApp a Coach</button>
    </div>
  `;

  document.getElementById('resultsOverlay').classList.add('show');
  document.getElementById('resultsModal').classList.add('show');
}

function makeRmCard(p) {
  return `
    <div class="rm-card" onclick="openQuickView('${p.id}')">
      <div class="rm-card-cat">${p.vendor}</div>
      <div class="rm-card-name">${p.name}</div>
      <div class="rm-card-why">${p.why}</div>
      <div class="rm-card-price">₹${p.price.toLocaleString('en-IN')}</div>
      <button class="rm-card-add" onclick="event.stopPropagation();addToCart('${p.id}')">+ Add to Cart</button>
    </div>`;
}

function closeResults() {
  document.getElementById('resultsOverlay').classList.remove('show');
  document.getElementById('resultsModal').classList.remove('show');
}

function renderPersonalSection() {
  const section = document.getElementById('personalSection');
  section.style.display = 'block';
  const stage = profile.stage || SPORT_CONFIG.stageOrder[1];
  document.getElementById('personalTitle').textContent = `Recommended for a ${STAGE_LABELS[stage]}`;

  const goalVal = profile.goal || profile.raceDistance;
  const volQ = SPORT_CONFIG.profileQuestions.find(q => q.type === 'slider');
  const volText = volQ && profile[volQ.id] ? `, ~${volQ.labels[profile[volQ.id]]}` : '';

  document.getElementById('personalSub').textContent =
    `Based on your profile — ${goalVal ? 'training towards ' + goalVal.replace(/-/g, ' ') : `your ${SPORT_CONFIG.label.toLowerCase()} journey`}${volText}. Update your profile anytime to refine these.`;

  const allCats = SPORT_CONFIG.categories.map(c => c.key).slice(0, 4);
  const picks = allCats.map(c => scoreForCategory(c, 1)[0]).filter(Boolean);
  document.getElementById('personalRail').innerHTML = picks.map(p => makeProductCard(p)).join('');
}

function openWA() {
  const stage = profile.stage ? STAGE_LABELS[profile.stage] : `a ${SPORT_CONFIG.label.toLowerCase()} athlete`;
  const goalVal = profile.goal || profile.raceDistance;
  const volQ = SPORT_CONFIG.profileQuestions.find(q => q.type === 'slider');
  const volText = volQ && profile[volQ.id] ? `, averaging ${volQ.labels[profile[volQ.id]]}` : '';
  const msg = `Hi triQUIP! I just built my ${SPORT_CONFIG.label.toLowerCase()} profile on the website. I'm ${stage}, training towards ${goalVal || 'general fitness'}${volText}. I'd love a coach's take on my recommendations.`;
  window.open(`https://wa.me/917977519164?text=${encodeURIComponent(msg)}`, '_blank');

}

// ════════════════════════════════════════════════════════════
// QUICK VIEW
// ════════════════════════════════════════════════════════════
function openQuickView(id) {
  const p = PRODUCTS.find(x => x.id === id);
  if (!p) return;
  const matchScore = profileMatchScore(p);
  document.getElementById('qvModal').innerHTML = `
    <div style="padding:32px">
      <div style="display:flex;gap:24px;margin-bottom:20px">
        <div>
          <div style="font-family:var(--bar);font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--dm);margin-bottom:4px">${p.vendor}</div>
          <div style="font:var(--bebas);font-size:28px;letter-spacing:.02em;color:var(--wh);margin-bottom:8px">${p.name}</div>
          <div style="font:var(--bebas);font-size:26px;color:var(--or);margin-bottom:8px">₹${p.price.toLocaleString('en-IN')}</div>
          <div style="font-family:var(--bar);font-size:12px;color:#FFAA00">${p.rating} rating</div>
          ${profile.hasProfile && matchScore > 60 ? '<div style="margin-top:8px;display:inline-block;background:rgba(0,230,138,.1);border:1px solid rgba(0,230,138,.3);color:var(--grn);font-family:var(--bar);font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;padding:4px 10px">Matches Your Profile</div>' : ''}
        </div>
      </div>
      <div style="font-size:13px;line-height:1.7;color:var(--bd);border-left:2px solid var(--or);padding-left:14px;margin-bottom:24px;font-style:italic">${p.why}</div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:24px">
        ${(p.discipline||[]).map(d=>`<span class="ptag match">${d}</span>`).join('')}
        ${(p.goal||[]).map(g=>`<span class="ptag">${g}</span>`).join('')}
      </div>
      <div style="display:flex;gap:10px">
        <button class="btn-pp-secondary" onclick="closeQuickView()">Close</button>
        <button class="btn-pp-primary" onclick="addToCart('${p.id}');closeQuickView()">Add to Cart — ₹${p.price.toLocaleString('en-IN')}</button>
      </div>
    </div>`;
  document.getElementById('qvOverlay').classList.add('show');
  document.getElementById('qvModal').classList.add('show');
}
function closeQuickView() {
  document.getElementById('qvOverlay').classList.remove('show');
  document.getElementById('qvModal').classList.remove('show');
}

// ════════════════════════════════════════════════════════════
// CART
// ════════════════════════════════════════════════════════════
function addToCart(id) {
  const p = PRODUCTS.find(x => x.id === id);
  if (!p) return;
  cartCount++;
  document.getElementById('cartBadge').textContent = cartCount;
  document.getElementById('sbName').textContent = p.name;
  document.getElementById('sbPrice').textContent = '₹' + p.price.toLocaleString('en-IN');
  const bar = document.getElementById('stickyBar');
  bar.classList.add('show');
  clearTimeout(bar._t);
  bar._t = setTimeout(() => bar.classList.remove('show'), 4500);
}
function dismissSticky() { document.getElementById('stickyBar').classList.remove('show'); }
function openCart() { /* placeholder cart drawer */ alert(`Cart: ${cartCount} item(s)`); }

// ════════════════════════════════════════════════════════════
// SCROLL SHADOW ON NAV
// ════════════════════════════════════════════════════════════
window.addEventListener('scroll', () => {
  document.getElementById('mainnav').style.boxShadow = window.scrollY > 60 ? '0 4px 28px rgba(0,0,0,.5)' : 'none';
});

function initReturningCustomer() {
  if (!CUSTOMER.isReturning) return;

  // Show "My Orders" pill in nav
  document.getElementById('orderHistoryPill').style.display = 'flex';

  // Soft, non-blocking nudge: change the invite strip copy for returning customers
  // instead of forcing a modal on load
  const inviteEl = document.querySelector('.invite-text');
  if (inviteEl && !sessionStorage.getItem('triquip_invite_dismissed')) {
    inviteEl.innerHTML = `<strong>Welcome back, ${CUSTOMER.name}</strong><span>You have ${getUnratedOrders().length} order${getUnratedOrders().length !== 1 ? 's' : ''} waiting for your feedback, and a few ideas on what's next — want to take a look?</span>`;
    document.querySelector('.invite-cta').textContent = 'See My Orders';
    document.querySelector('.invite-cta').setAttribute('onclick', 'openWelcomeBack()');
  }
}